import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigation, useRoute } from '@react-navigation/native';
import { MaterialIcons } from '@expo/vector-icons';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { StyleSheet, Text, View } from 'react-native';
import * as yup from 'yup';
import {
  AuthShell,
  CountdownTimer,
  IconTextField,
  OTPInput,
  PrimaryCTA,
} from '../../components';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { fpColors } from '../../styles/theme';
import {
  clearError,
  requestPasswordReset,
  resetPassword,
  selectAuthError,
  selectIsResettingPassword,
  selectValidationErrors,
  selectVerificationExpiresAt,
  selectVerificationToken,
} from './authSlice';

const emailSchema = yup.object({
  email: yup
    .string()
    .email('Please enter a valid email address')
    .required('Email is required'),
});

const resetSchema = yup.object({
  code: yup
    .string()
    .matches(/^\d{6}$/, 'Verification code must be 6 digits')
    .required('Verification code is required'),
  newPassword: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .required('New password is required'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('newPassword')], 'Passwords must match')
    .required('Please confirm your password'),
});

type Step = 'email' | 'reset';

export const ForgotPasswordScreen: React.FC = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const dispatch = useAppDispatch();

  // FE-168: ChangePasswordScreen can pre-fill the email so an
  // already-signed-in user (Google-only or forgetful) doesn't have
  // to retype it. Falls back to empty when reached from the Login
  // screen's "Forgot password?" link.
  const presetEmail =
    (route.params as { email?: string } | undefined)?.email ?? '';

  const [step, setStep] = useState<Step>('email');
  const [userEmail, setUserEmail] = useState(presetEmail);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const isLoading = useAppSelector(selectIsResettingPassword);
  const error = useAppSelector(selectAuthError);
  const validationErrors = useAppSelector(selectValidationErrors);
  const verificationToken = useAppSelector(selectVerificationToken);
  const verificationExpiresAt = useAppSelector(selectVerificationExpiresAt);

  const emailForm = useForm<{ email: string }>({
    resolver: yupResolver(emailSchema),
    defaultValues: { email: presetEmail },
  });

  const resetForm = useForm<{
    code: string;
    newPassword: string;
    confirmPassword: string;
  }>({
    resolver: yupResolver(resetSchema),
    defaultValues: { code: '', newPassword: '', confirmPassword: '' },
  });

  const codeValue = resetForm.watch('code');

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const remainingSeconds = verificationExpiresAt
    ? Math.max(0, Math.floor((verificationExpiresAt - Date.now()) / 1000))
    : 1800;

  const onEmailSubmit = async ({ email }: { email: string }) => {
    const result = await dispatch(requestPasswordReset(email));
    if (requestPasswordReset.fulfilled.match(result)) {
      setUserEmail(email);
      setStep('reset');
    }
  };

  const onResetSubmit = async (data: {
    code: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    if (!verificationToken) return;
    const result = await dispatch(
      resetPassword({
        code: data.code,
        newPassword: data.newPassword,
        token: verificationToken,
      }),
    );
    if (resetPassword.fulfilled.match(result)) {
      (navigation as any).navigate('Login');
    }
  };

  const handleBack = () => {
    if (step === 'reset') {
      setStep('email');
      return;
    }
    // Honor history when present (e.g. arriving from ChangePasswordScreen)
    // and fall back to a hard nav to Login for the original Login-screen
    // entry point + deep-link cases. Without this, ChangePassword →
    // ForgotPassword → Back would kick a signed-in user to Login.
    if ((navigation as any).canGoBack?.()) {
      (navigation as any).goBack();
    } else {
      (navigation as any).navigate('Login');
    }
  };

  const errorText = (() => {
    if (typeof error === 'string' && error.length > 0) return error;
    if (validationErrors) {
      if (typeof validationErrors === 'string') return validationErrors;
      if (Array.isArray(validationErrors) && validationErrors.length > 0) {
        const first = validationErrors[0];
        return typeof first === 'string' ? first : (first as any)?.message ?? null;
      }
    }
    return null;
  })();

  return (
    <AuthShell showBack onBack={handleBack}>
      <View style={styles.headingBlock}>
        <View style={styles.iconBadge}>
          <MaterialIcons
            name={step === 'email' ? 'lock-open' : 'mark-email-read'}
            size={28}
            color={fpColors.primary}
          />
        </View>
        <Text style={styles.h1}>
          {step === 'email' ? 'Reset your password' : 'Enter reset code'}
        </Text>
        <Text style={styles.subtitle}>
          {step === 'email'
            ? "Enter your email and we'll send a 6-digit code to reset your password."
            : `Enter the 6-digit code sent to ${userEmail} and set a new password.`}
        </Text>
      </View>

      {step === 'email' ? (
        <View style={styles.form}>
          <Controller
            control={emailForm.control}
            name="email"
            render={({ field: { onChange, onBlur, value } }) => (
              <IconTextField
                label="Email Address"
                leftIcon="mail"
                placeholder="name@example.com"
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                error={emailForm.formState.errors.email?.message}
                keyboardType="email-address"
                autoCapitalize="none"
                autoComplete="email"
                autoFocus
              />
            )}
          />

          {errorText ? (
            <View style={styles.errorBanner}>
              <MaterialIcons
                name="error-outline"
                size={18}
                color={fpColors.error}
              />
              <Text style={styles.errorBannerText}>{errorText}</Text>
            </View>
          ) : null}

          <PrimaryCTA
            label="Send reset code"
            onPress={emailForm.handleSubmit(onEmailSubmit)}
            loading={isLoading}
          />
        </View>
      ) : (
        <View style={styles.form}>
          <View>
            <Text style={styles.sectionLabel}>Verification code</Text>
            <Controller
              control={resetForm.control}
              name="code"
              render={({ field: { value } }) => (
                <OTPInput
                  value={value}
                  onChange={(v) => resetForm.setValue('code', v)}
                  error={resetForm.formState.errors.code?.message}
                  autoFocus
                />
              )}
            />
            <View style={styles.timerRow}>
              <Text style={styles.timerLabel}>Code expires in </Text>
              <CountdownTimer
                initialSeconds={remainingSeconds}
                format="mm:ss"
                style={styles.timer}
                onExpire={() => {}}
              />
            </View>
          </View>

          <Controller
            control={resetForm.control}
            name="newPassword"
            render={({ field: { onChange, onBlur, value } }) => (
              <IconTextField
                label="New password"
                leftIcon="lock"
                rightIcon={showPassword ? 'visibility-off' : 'visibility'}
                onRightIconPress={() => setShowPassword((v) => !v)}
                placeholder="At least 8 characters"
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                error={resetForm.formState.errors.newPassword?.message}
                secureTextEntry={!showPassword}
                autoCapitalize="none"
                autoComplete="new-password"
              />
            )}
          />

          <Controller
            control={resetForm.control}
            name="confirmPassword"
            render={({ field: { onChange, onBlur, value } }) => (
              <IconTextField
                label="Confirm new password"
                leftIcon="lock"
                rightIcon={showConfirm ? 'visibility-off' : 'visibility'}
                onRightIconPress={() => setShowConfirm((v) => !v)}
                placeholder="Re-enter password"
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                error={resetForm.formState.errors.confirmPassword?.message}
                secureTextEntry={!showConfirm}
                autoCapitalize="none"
                autoComplete="new-password"
              />
            )}
          />

          {errorText ? (
            <View style={styles.errorBanner}>
              <MaterialIcons
                name="error-outline"
                size={18}
                color={fpColors.error}
              />
              <Text style={styles.errorBannerText}>{errorText}</Text>
            </View>
          ) : null}

          <PrimaryCTA
            label="Update password"
            onPress={resetForm.handleSubmit(onResetSubmit)}
            loading={isLoading}
            disabled={codeValue.length !== 6}
          />
        </View>
      )}

      <Text style={styles.footerText}>
        Remember your password?{' '}
        <Text
          style={styles.footerLink}
          onPress={() => (navigation as any).navigate('Login')}
        >
          Back to sign in
        </Text>
      </Text>
    </AuthShell>
  );
};

const styles = StyleSheet.create({
  headingBlock: {
    marginBottom: 16,
    alignItems: 'center',
  },
  iconBadge: {
    width: 44,
    height: 44,
    borderRadius: 14,
    backgroundColor: fpColors.surfaceContainerLow,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  h1: {
    fontFamily: 'Inter_700Bold',
    fontSize: 22,
    color: fpColors.onBackground,
    marginBottom: 4,
    textAlign: 'center',
  },
  subtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    lineHeight: 18,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
  },
  form: {
    gap: 12,
  },
  sectionLabel: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.onSurface,
    marginBottom: 8,
  },
  timerRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 6,
  },
  timerLabel: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
  },
  timer: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.primary,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 12,
    borderRadius: 10,
    backgroundColor: fpColors.errorContainer,
  },
  errorBannerText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onErrorContainer,
  },
  footerText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
    marginTop: 16,
  },
  footerLink: {
    fontFamily: 'Inter_700Bold',
    color: fpColors.primary,
  },
});
